import { baseApi } from '../../../shared/api/baseApi';
import type { ApiPath, Paginated } from '../../../shared/api/types';
import { notifyOnError } from '../../../shared/api/onQueryError';
import type { QueueItem, TypicalSize } from '../../../types/sizes';

const SIZES = '/api/admin/catalogs/standard_sizes';
const REPORT = '/api/admin/standard_size_report';

interface StandardSizeDto {
    id: string;
    name: string;
    name_raw: string;
    length: number | null;
    width: number | null;
    height: number | null;
    maximum_load_weight: number | null;
    size_units: string;
    weight_units: string;
    source: string | null;
    user_name: string | null;
    created_at: string;

    transport_length?: number | null;
    transport_width?: number | null;
    transport_height?: number | null;
    transport_weight?: number | null;
    packaging_class?: string | null;
    transport_is_manual?: boolean;
    origin?: string | null;
    source_title?: string | null;
    source_titles?: string[] | null;
    image_url?: string | null;
}

interface QueueItemDto {
    id: string;
    name: string;
    project_id: string | null;
    project_name: string | null;
    /** Страница товара, откуда взяты габариты. */
    url: string | null;
    length: number | null;
    width: number | null;
    height: number | null;
    maximum_load_weight: number | null;
    size_units: string;
    weight_units: string;
    created_at: string;

    transport_length?: number | null;
    transport_width?: number | null;
    transport_height?: number | null;
    transport_weight?: number | null;
    packaging_class?: string | null;
    transport_is_manual?: boolean;
    source_titles?: string[] | null;
    image_url?: string | null;
}

export interface SizesPage<T> {
    items: T[];
    total: number;
    pages: number;
}

export interface SizesParams {
    page_num?: number;
    page_size?: number;
    query?: string;
}

export interface CreateStandardSizeBody {
    name: string;
    length: number | null;
    width: number | null;
    height: number | null;
    maximum_load_weight: number | null;
    transport_length?: number;
    transport_width?: number;
    transport_height?: number;
    transport_weight?: number;
    source_titles?: string[];
}

function mapSize(dto: StandardSizeDto): TypicalSize {
    return {
        id: dto.id,
        name: dto.name,
        nameRaw: dto.name_raw,
        length: dto.length ?? null,
        width: dto.width ?? null,
        height: dto.height ?? null,
        weight: dto.maximum_load_weight ?? null,
        sizeUnits: dto.size_units || 'мм',
        weightUnits: dto.weight_units || 'г',
        source: dto.source ?? null,
        author: dto.user_name ?? null,
        createdAt: dto.created_at,
        transportLength: dto.transport_length ?? null,
        transportWidth: dto.transport_width ?? null,
        transportHeight: dto.transport_height ?? null,
        transportWeight: dto.transport_weight ?? null,
        packagingClass: dto.packaging_class ?? null,
        transportIsManual: Boolean(dto.transport_is_manual),
        origin: dto.origin ?? null,
        sourceTitle: dto.source_title ?? null,
        sourceTitles: dto.source_titles ?? [],
        imageUrl: dto.image_url || null,
    };
}

function mapQueueItem(dto: QueueItemDto): QueueItem {
    return {
        id: dto.id,
        name: dto.name,
        projectId: dto.project_id ?? null,
        projectName: dto.project_name ?? '—',
        url: dto.url ?? null,
        length: dto.length ?? null,
        width: dto.width ?? null,
        height: dto.height ?? null,
        weight: dto.maximum_load_weight ?? null,
        sizeUnits: dto.size_units || 'мм',
        weightUnits: dto.weight_units || 'г',
        createdAt: dto.created_at,
        transportLength: dto.transport_length ?? null,
        transportWidth: dto.transport_width ?? null,
        transportHeight: dto.transport_height ?? null,
        transportWeight: dto.transport_weight ?? null,
        packagingClass: dto.packaging_class ?? null,
        transportIsManual: Boolean(dto.transport_is_manual),
        sourceTitles: dto.source_titles ?? [],
        imageUrl: dto.image_url || null,
    };
}

export const sizeApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getStandardSizes: builder.query<SizesPage<TypicalSize>, SizesParams>({
            query: ({ page_num = 1, page_size = 10, query = '' }) => ({
                url: SIZES satisfies ApiPath,
                params: { page_num, page_size, query },
            }),
            transformResponse: (response: Paginated<StandardSizeDto>) => ({
                items: (response.data ?? []).map(mapSize),
                total: response.count ?? 0,
                pages: response.pages ?? 1,
            }),
            providesTags: ['StandardSize'],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось загрузить типовые размеры');
            },
        }),

        getSizeQueue: builder.query<SizesPage<QueueItem>, SizesParams>({
            query: ({ page_num = 1, page_size = 10, query = '' }) => ({
                url: REPORT satisfies ApiPath,
                params: { page_num, page_size, query },
            }),
            transformResponse: (response: Paginated<QueueItemDto>) => ({
                items: (response.data ?? []).map(mapQueueItem),
                total: response.count ?? 0,
                pages: response.pages ?? 1,
            }),
            providesTags: ['SizeQueue'],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось загрузить товары для добавления');
            },
        }),

        addToHandbook: builder.mutation<unknown, string[]>({
            query: (ids) => ({
                url: `${REPORT}/add` satisfies ApiPath,
                method: 'POST',
                body: { standard_sizes_ids: ids },
            }),
            invalidatesTags: ['StandardSize', 'SizeQueue'],
        }),

        removeFromQueue: builder.mutation<unknown, string[]>({
            query: (ids) => ({
                url: `${REPORT}/delete` satisfies ApiPath,
                method: 'POST',
                body: { standard_sizes_ids: ids },
            }),
            invalidatesTags: ['SizeQueue'],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось убрать из очереди');
            },
        }),

        /* Пути подтверждены по старому фронту: handbookAPIs.js. */
        /**
         * Создание записи справочника вручную.
         *
         * Единицы измерения обязательны и допускают ровно одно значение
         * каждое, поэтому шлём их константами. Имя бэкенд нормализует —
         * показывать нужно значение из ответа, а не из формы.
         */
        createStandardSize: builder.mutation<TypicalSize, CreateStandardSizeBody>({
            query: (body) => ({
                url: SIZES satisfies ApiPath,
                method: 'POST',
                body: { ...body, size_units: 'мм', weight_units: 'г' },
            }),
            transformResponse: (response: StandardSizeDto) => mapSize(response),
            invalidatesTags: ['StandardSize'],
        }),

        updateStandardSize: builder.mutation<unknown, { id: string; body: Record<string, unknown> }>({
            query: ({ id, body }) => ({
                url: `${SIZES}/${id}` satisfies ApiPath,
                method: 'PATCH',
                body,
            }),
            transformResponse: (response: StandardSizeDto) => mapSize(response),
            invalidatesTags: ['StandardSize'],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось сохранить типовой размер');
            },
        }),

        updateQueueItem: builder.mutation<QueueItem, { id: string; body: Record<string, unknown> }>({
            query: ({ id, body }) => ({
                url: `${REPORT}/${id}` satisfies ApiPath,
                method: 'PATCH',
                body,
            }),
            transformResponse: (response: QueueItemDto) => mapQueueItem(response),
            invalidatesTags: ['SizeQueue'],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось сохранить габариты');
            },
        }),

        deleteStandardSize: builder.mutation<unknown, string>({
            query: (id) => ({ url: `${SIZES}/${id}` satisfies ApiPath, method: 'DELETE' }),
            invalidatesTags: ['StandardSize'],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось удалить типовой размер');
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetStandardSizesQuery,
    useLazyGetStandardSizesQuery,
    useAddToHandbookMutation,
    useCreateStandardSizeMutation,
    useRemoveFromQueueMutation,
    useGetSizeQueueQuery,
    useUpdateStandardSizeMutation,
    useUpdateQueueItemMutation,
    useDeleteStandardSizeMutation,
} = sizeApi;